import { Box, Checkbox, Chip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import {
  ariaDescribedByIds,
  enumOptionsIndexForValue,
  enumOptionsValueForIndex,
  FormContextType,
  labelValue,
  RJSFSchema,
  StrictRJSFSchema,
  WidgetProps,
} from '@rjsf/utils';
import { ChangeEvent, FocusEvent, useEffect, useMemo, useState } from 'react';
import { resolveDynamicOptions } from '../utils/utils';

export default function CustomSelectWidget<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any
>({
  schema,
  id,
  name,
  options,
  label,
  hideLabel,
  required,
  disabled,
  placeholder,
  readonly,
  value,
  multiple,
  autofocus,
  onChange,
  onBlur,
  onFocus,
  errorSchema,
  rawErrors = [],
  registry,
  uiSchema,
  hideError,
  formContext,
  ...textFieldProps
}: WidgetProps<T, S, F>) {
  const dynamicExpression = uiSchema?.["ui:options"]?.dynamicExpression;

  const [dynamicEnumOptions, setDynamicEnumOptions] = useState<any[]>([]);

  useEffect(() => {
    if (dynamicExpression && typeof dynamicExpression === 'string' && formContext) {
      const hasFormData = formContext && Object.keys(formContext).some(key => key !== 'globalSchema');
      if (!hasFormData) {
        return;
      }
      const resolved = resolveDynamicOptions(dynamicExpression, formContext);
      setDynamicEnumOptions(resolved.map((opt, idx) => ({
        value: opt.value,
        label: opt.label
      })));
    }
  }, [dynamicExpression, formContext]);

  const enumOptions = useMemo(() => {
    if (dynamicExpression) {
      return dynamicEnumOptions;
    }
    return options.enumOptions;
  }, [dynamicExpression, dynamicEnumOptions, options.enumOptions]);

  const { enumDisabled, emptyValue: optEmptyVal } = options;

  multiple = typeof multiple === 'undefined' ? false : !!multiple;

  let emptyValue = multiple ? [] : '';
  const isEmpty = typeof value === 'undefined' || (multiple && value.length < 1) || (!multiple && value === emptyValue);

  // Clear invalid values: if we have a value that is not in enumOptions, treat it as empty
  useEffect(() => {
    // Only clear if we have a non-empty value that is not in the options
    if (!isEmpty && value !== null && value !== undefined && enumOptions && enumOptions.length > 0) {
      const selectedIndexes = enumOptionsIndexForValue<S>(value, enumOptions, multiple);
      if (selectedIndexes === undefined || (Array.isArray(selectedIndexes) && selectedIndexes.length === 0)) {
        // Value is not in the enum options - clear it only if it's not already empty
        const currentIsEmpty = multiple ? (Array.isArray(value) && value.length === 0) : (value === '');
        if (!currentIsEmpty) {
          onChange(emptyValue as any);
        }
      }
    }
  }, [value, enumOptions, multiple, isEmpty, emptyValue, onChange]);

  const _onChange = ({ target: { value } }: ChangeEvent<{ value: string }>) =>
    onChange(enumOptionsValueForIndex<S>(value, enumOptions, optEmptyVal));
  const _onBlur = ({ target }: FocusEvent<HTMLInputElement>) =>
    onBlur(id, enumOptionsValueForIndex<S>(target && target.value, enumOptions, optEmptyVal));
  const _onFocus = ({ target }: FocusEvent<HTMLInputElement>) =>
    onFocus(id, enumOptionsValueForIndex<S>(target && target.value, enumOptions, optEmptyVal));
  const selectedIndexes = enumOptionsIndexForValue<S>(value, enumOptions, multiple);
  const { InputLabelProps, SelectProps, autocomplete, ...textFieldRemainingProps } = textFieldProps;
  const showPlaceholderOption = !multiple && schema.default === undefined;

  return (
    <TextField
      id={id}
      name={id}
      fullWidth
      label={labelValue(label || undefined, hideLabel, undefined)}
      value={!isEmpty && typeof selectedIndexes !== 'undefined'
        ? selectedIndexes
        : emptyValue
      }
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      autoComplete={autocomplete}
      placeholder={placeholder}
      multiline={true}
      error={rawErrors.length > 0}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
      {...(textFieldRemainingProps as TextFieldProps)}
      select
      InputLabelProps={{
        ...InputLabelProps,
        shrink: !isEmpty,
      }}
      SelectProps={{
        ...SelectProps,
        multiple,
        renderValue: (selected: string | string[]) => (
          Array.isArray(selected) ?
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value: any) => (
                <Chip key={value} label={enumOptions?.[Number(value)]?.label} />
              ))}
            </Box>
            : enumOptions?.[Number(selected)]?.label
        )
      }}
      aria-describedby={ariaDescribedByIds<T>(id)}
    >
      {showPlaceholderOption && <MenuItem value=''>{placeholder}</MenuItem>}
      {Array.isArray(enumOptions) &&
        enumOptions.map(({ value, label }, i: number) => {
          const disabled: boolean = Array.isArray(enumDisabled) && enumDisabled.indexOf(value) !== -1;
          return (
            <MenuItem key={i} value={String(i)} disabled={disabled}>
              {multiple && <Checkbox checked={selectedIndexes?.includes(i.toString())} />}
              {label}
            </MenuItem>
          );
        })}
    </TextField>
  );
}
